import { z } from 'zod';

import { renderArtifactOutputSchema } from '@apps/web/lib/agent_render_artifact';

const PRESENTATION_PATH_PATTERN =
    /^\/workspace\/presentations\/[a-f0-9]{24}\.jsonl$/;
const SANDBOX_QUERY_PRESENTATION_PATH_PATTERN =
    /^\/workspace\/tool_outputs\/[0-9]{4,}_query\.jsonl$/;

export const dataPresentationSchema = z.enum([
    'shifts',
    'caregivers',
    'client_detail',
]);

const bashPresentationReceiptSchema = z.object({
    presentation: dataPresentationSchema,
    artifact_path: z.string().min(1),
    format: z.literal('jsonl'),
    row_count: z.number().int().nonnegative(),
    byte_size: z.number().int().nonnegative(),
    columns: z.array(z.string().max(128)).max(64),
    columns_truncated: z.boolean(),
    content_sha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .optional(),
    event_id: z.uuid().optional(),
});

const bashPresentationOutputSchema = z.object({
    presentation_artifact: bashPresentationReceiptSchema,
});

export type DataPresentation = z.infer<typeof dataPresentationSchema>;
export type DataPresentationReceipt = z.infer<
    typeof bashPresentationReceiptSchema
>;
type ParsedPresentationOutputBase = {
    readonly receipt: DataPresentationReceipt;
    readonly workspacePath: string;
};

export type DataPresentationSource = 'bash' | 'query';

export type ParsedDataPresentationOutput = ParsedPresentationOutputBase & {
    readonly source: DataPresentationSource;
};

export function parseDataPresentationOutput(
    toolName: string,
    output: unknown,
): ParsedDataPresentationOutput | null {
    const parsed = bashPresentationOutputSchema.safeParse(output);
    const renderParsed =
        toolName === 'render_dynamic_ui'
            ? renderArtifactOutputSchema.safeParse(output)
            : { success: false as const };
    const receipt = parsed.success
        ? parsed.data.presentation_artifact
        : renderParsed.success &&
            renderParsed.data.render_artifact.component !== 'table'
          ? {
                presentation: renderParsed.data.render_artifact.component,
                artifact_path: renderParsed.data.render_artifact.path,
                format: 'jsonl' as const,
                row_count: renderParsed.data.render_artifact.row_count,
                byte_size: renderParsed.data.render_artifact.byte_size,
                columns: renderParsed.data.render_artifact.columns,
                columns_truncated:
                    renderParsed.data.render_artifact.columns_truncated,
                content_sha256:
                    renderParsed.data.render_artifact.content_sha256,
                event_id: renderParsed.data.render_artifact.event_id,
            }
          : null;
    if (receipt === null) {
        return null;
    }
    const artifactPath = receipt.artifact_path.trim();
    if (
        toolName !== 'run_bash' &&
        toolName !== 'render_dynamic_ui' &&
        toolName !== 'query'
    ) {
        return null;
    }
    const source = toolName === 'query' ? 'query' : 'bash';
    const pathIsAllowed =
        PRESENTATION_PATH_PATTERN.test(artifactPath) ||
        (source === 'query' &&
            SANDBOX_QUERY_PRESENTATION_PATH_PATTERN.test(artifactPath));
    if (
        !pathIsAllowed ||
        (receipt.presentation === 'client_detail' && receipt.row_count !== 1)
    ) {
        return null;
    }
    return {
        receipt: { ...receipt, artifact_path: artifactPath },
        workspacePath: artifactPath.slice('/workspace/'.length),
        source,
    };
}
